import React, { createContext, useContext, useState, ReactNode } from 'react';

// Define the shape of our context data
interface UnlockContextType {
  isUnlocked: boolean;
  setIsUnlocked: (value: boolean) => void;
}

// Create the Context - this is like creating a "radio channel" that components can tune into
const UnlockContext = createContext<UnlockContextType | undefined>(undefined);

// Provider Component - this holds the actual state and broadcasts it to all children
export const UnlockProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // This is the single source of truth for the unlock state
  const [isUnlocked, setIsUnlocked] = useState<boolean>(false);

  // Wrap children with the Provider so they can access the state
  return (
    <UnlockContext.Provider value={{ isUnlocked, setIsUnlocked }}>
      {children}
    </UnlockContext.Provider>
  );
};

// Custom Hook - makes it easy for components to access the unlock state
// Any component that calls useUnlock() will automatically re-render when isUnlocked changes
export const useUnlock = () => {
  const context = useContext(UnlockContext);
  
  // Safety check: make sure this hook is only used inside UnlockProvider
  if (!context) {
    throw new Error('useUnlock must be used within UnlockProvider');
  }
  
  return context;
};